import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import {
  projectsCuisinesData,
  projectsDressings,
  projectsCuisinesIntData,
} from "../../Pages/Collections/CollectionsData";
import "./SearchBar.css";

/* ==================================================
   INDEX DE RECHERCHE
   - pages du site
   - projets (construits automatiquement depuis CollectionsData)
================================================== */
const PAGES = [
  { title: "Notre histoire", path: "/elements/histoire", keywords: "elements group a propos entreprise" },
  { title: "Nos créateurs", path: "/elements/createurs", keywords: "equipe designers elements group" },
  { title: "L'atelier", path: "/elements/atelier", keywords: "fabrication production savoir-faire" },
  { title: "Partenaires", path: "/elements/partenaires", keywords: "marques fournisseurs" },
  { title: "Cuisines sur mesure", path: "/collections/cuisines", keywords: "collections cuisine" },
  { title: "Dressings sur mesure", path: "/collections/dressings", keywords: "collections dressing rangement" },
  { title: "Aménagement intérieur", path: "/collections/amenagement", keywords: "collections salon salle de bain" },
  { title: "Accessoires", path: "/collections/accessoires", keywords: "collections poignees rangements" },
  { title: "Textures & finitions", path: "/collections/textures", keywords: "collections materiaux couleurs bois gres" },
  { title: "Kit de nettoyage", path: "/sav/kit-nettoyage", keywords: "sav entretien produits" },
  { title: "Garantie", path: "/sav/garantie", keywords: "sav" },
  { title: "Service après-vente", path: "/sav/service-apres-vente", keywords: "sav assistance reclamation" },
  { title: "Projets internationaux", path: "/export/projets-international", keywords: "export monde france realisations" },
  { title: "Témoignages clients", path: "/export/temoignages-clients", keywords: "export avis" },
  { title: "Carte des réalisations", path: "/export/carte", keywords: "export map localisation" },
  { title: "Contact", path: "/contact", keywords: "adresse telephone email devis" },
];

const SUGGESTIONS = [
  { title: "Cuisines", path: "/collections/cuisines" },
  { title: "Dressings", path: "/collections/dressings" },
  { title: "Textures", path: "/collections/textures" },
  { title: "Projets internationaux", path: "/export/projets-international" },
  { title: "Garantie", path: "/sav/garantie" },
  { title: "Contact", path: "/contact" },
];

const buildProjects = (data, type, basePath, keywords) =>
  Object.entries(data || {}).map(([key, p]) => ({
    type,
    title: p.title,
    path: `${basePath}/${key}`,
    keywords,
    text: `${p.description || ""} ${(p.finishes || []).map((f) => f.name).join(" ")}`,
  }));

/* Supprime accents + minuscules, en gardant 1 caractère pour 1 caractère
   (nécessaire pour surligner correctement les résultats). */
const foldChar = (c) => {
  const f = c.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  return f.length === 1 ? f : c.toLowerCase().charAt(0) || c;
};
const fold = (str) => Array.from(str || "").map(foldChar).join("");

const SEARCH_INDEX = [
  ...PAGES.map((p) => ({ ...p, type: "Page", text: "" })),
  ...buildProjects(projectsCuisinesData, "Cuisines", "/collections/cuisines", "cuisine projet realisation"),
  ...buildProjects(projectsDressings, "Dressings", "/collections/dressings", "dressing projet realisation"),
  ...buildProjects(projectsCuisinesIntData, "Export", "/export/projets-international", "cuisine projet international export realisation"),
].map((item) => ({
  ...item,
  _title: fold(item.title),
  _rest: fold(`${item.keywords || ""} ${item.text || ""} ${item.type}`),
}));

const search = (query) => {
  const tokens = fold(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return [];

  return SEARCH_INDEX.map((item) => {
    let score = 0;
    for (const t of tokens) {
      if (item._title.includes(t)) {
        score += item._title.startsWith(t) ? 12 : 10;
      } else if (item._rest.includes(t)) {
        score += 2;
      } else {
        return null; // tous les mots doivent être trouvés
      }
    }
    return { ...item, score };
  })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);
};

/* Surligne les mots trouvés dans le titre */
const Highlight = ({ text, query }) => {
  const tokens = fold(query).split(/\s+/).filter(Boolean);
  if (!tokens.length) return text;

  const chars = Array.from(text);
  const folded = fold(text);
  const marks = new Array(chars.length).fill(false);

  tokens.forEach((t) => {
    let from = 0;
    let idx;
    while ((idx = folded.indexOf(t, from)) !== -1) {
      for (let i = idx; i < idx + t.length; i++) marks[i] = true;
      from = idx + t.length;
    }
  });

  const out = [];
  let buf = "";
  let current = marks[0];
  chars.forEach((c, i) => {
    if (marks[i] !== current) {
      out.push(current ? <mark key={i}>{buf}</mark> : buf);
      buf = "";
      current = marks[i];
    }
    buf += c;
  });
  out.push(current ? <mark key="last">{buf}</mark> : buf);
  return <>{out}</>;
};

/* ==================================================
   COMPOSANT
================================================== */
export default function SearchBar({ navigateWithTransition }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const location = useLocation();

  const results = useMemo(() => search(query), [query]);
  const hasQuery = query.trim().length > 0;

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setActive(0);
  }, []);

  const go = useCallback(
    (path) => {
      close();
      if (path !== location.pathname) navigateWithTransition(path);
    },
    [close, location.pathname, navigateWithTransition]
  );

  /* Bloque le scroll + focus de l'input */
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = setTimeout(() => inputRef.current?.focus(), 120);
    return () => {
      clearTimeout(t);
      document.body.style.overflow = previous;
    };
  }, [open]);

  /* Raccourcis clavier : Ctrl/Cmd + K pour ouvrir, Échap pour fermer */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape") {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  useEffect(() => setActive(0), [query]);

  const onInputKeyDown = (e) => {
    const list = hasQuery ? results : [];
    if (e.key === "ArrowDown" && list.length) {
      e.preventDefault();
      setActive((a) => (a + 1) % list.length);
    } else if (e.key === "ArrowUp" && list.length) {
      e.preventDefault();
      setActive((a) => (a - 1 + list.length) % list.length);
    } else if (e.key === "Enter" && list[active]) {
      e.preventDefault();
      go(list[active].path);
    }
  };

  const overlay = (
    <div
      className={`search-overlay ${open ? "open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Recherche"
      aria-hidden={!open}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <button className="search-close" onClick={close} aria-label="Fermer la recherche">
        <CloseIcon />
      </button>

      <div className="search-inner">
        <span className="search-eyebrow">Rechercher</span>

        <div className="search-field">
          <SearchIcon className="search-field-icon" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Une cuisine, un dressing, une finition…"
            aria-label="Rechercher sur le site"
            aria-controls="search-results"
            aria-activedescendant={hasQuery && results[active] ? `search-result-${active}` : undefined}
            autoComplete="off"
            spellCheck="false"
          />
          {hasQuery && (
            <button className="search-clear" onClick={() => { setQuery(""); inputRef.current?.focus(); }}>
              Effacer
            </button>
          )}
        </div>

        <div className="search-body" data-lenis-prevent>
          {!hasQuery && (
            <div className="search-suggestions">
              <span className="search-label">Accès rapide</span>
              <div className="search-pills">
                {SUGGESTIONS.map((s) => (
                  <button key={s.path} className="search-pill" onClick={() => go(s.path)}>
                    {s.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          {hasQuery && results.length > 0 && (
            <ul className="search-results" id="search-results" role="listbox">
              {results.map((r, i) => (
                <li
                  key={r.path}
                  id={`search-result-${i}`}
                  role="option"
                  aria-selected={i === active}
                  className={i === active ? "active" : ""}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => go(r.path)}
                  style={{ "--d": `${i * 0.04}s` }}
                >
                  <span className="search-result-type">{r.type}</span>
                  <span className="search-result-title">
                    <Highlight text={r.title} query={query} />
                  </span>
                  <span className="search-result-arrow" aria-hidden="true">→</span>
                </li>
              ))}
            </ul>
          )}

          {hasQuery && results.length === 0 && (
            <p className="search-empty">
              Aucun résultat pour « {query.trim()} ».
              <button className="search-pill" onClick={() => go("/contact")}>Nous contacter</button>
            </p>
          )}
        </div>

        <div className="search-hints" aria-hidden="true">
          <span><kbd>↑</kbd><kbd>↓</kbd> naviguer</span>
          <span><kbd>Entrée</kbd> ouvrir</span>
          <span><kbd>Échap</kbd> fermer</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        className="search-trigger"
        onClick={() => setOpen(true)}
        aria-label="Ouvrir la recherche"
        aria-haspopup="dialog"
      >
        <SearchIcon />
      </button>
      {createPortal(overlay, document.body)}
    </>
  );
}
